import { useEffect, useState } from 'react';
import axiosInstance from '../services/api';

/**
 * サーバから返却される接続元分類
 * 'internal' = 院内ネットワーク（localhost / 院内 LAN）
 * 'external' = 院外ネットワーク（Tailscale 100.x / ULA 等）
 */
type Origin = 'internal' | 'external';

interface OriginAccessState {
  /** サーバが返した接続元分類。取得前または失敗時は null */
  origin: Origin | null;
  /** 院外接続かどうか。origin==='external' の場合のみ true */
  isExternal: boolean;
  /** GET /api/access/origin の通信中は true */
  isLoading: boolean;
}

/**
 * 画面初期化時にサーバの接続元分類 API を 1 回だけ取得するフック。
 *
 * 【設計方針】
 * - 接続元の正本はサーバ側判定（`classifyOrigin` / `isInternal`）であり、
 *   クライアントが独自に IP を判定することは禁止。
 * - 取得失敗時はフロント側での UI 制限を行わず、ボタンを活性のままにして
 *   サーバの 403 に委ねる（サイレント失敗回避・閲覧体験最優先）。
 *   判定不能時の「とりあえず制限」は院内ユーザーを誤拒否するリスクがある。
 * - download/upload の最終的なアクセス制御はバックエンドの
 *   requireInternalNetwork ミドルウェアが担保する（フロントはあくまで UX）。
 *
 * 仕様書 §2.9 / 設計書 §3.2 参照。
 */
export const useOriginAccess = (): OriginAccessState => {
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    const fetchOrigin = async (): Promise<void> => {
      try {
        const response = await axiosInstance.get<{ origin: Origin }>('/access/origin');
        if (!cancelled) {
          setOrigin(response.data.origin);
        }
      } catch (error) {
        // 取得失敗時は origin を null のままにする（UI 制限はかけない）。
        // フロントで制限をかけると院内ユーザーを誤拒否しうるため、
        // 制限判断はサーバ(requireInternalNetwork)に完全に委ねる。
        // ただし完全な無音は「院内なのにバナーが出ない / DL ボタンが活性のまま 403」等の
        // 原因究明を困難にするため、警告ログだけは残す（UI 挙動は変えない）。
        console.warn('[useOriginAccess] 接続元分類の取得に失敗しました。サーバ判定に委ねます。', error);
        if (!cancelled) {
          setOrigin(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchOrigin();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    origin,
    // origin が null（未取得・失敗）の場合は false とし UI 制限を行わない。
    // 正本はサーバ判定なので、不明時はボタンを活性のまま 403 に委ねる。
    isExternal: origin === 'external',
    isLoading,
  };
};
